import React, { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "../lib/api";
import { Sparkles, Mail, Calendar, X, RefreshCw, TrendingUp, Send, Repeat, AlertTriangle, CheckCircle2 } from "lucide-react";

const WEEKDAYS_IT = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const CADENCE_LABELS = { weekly: "settimanale", biweekly: "ogni 2 settimane", monthly: "mensile", monthly_plus: "ogni 30-50g" };

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("it-IT", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}
function fmtDateShort(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long" });
}
function todayYmd() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
function addDaysYmd(ymd, n) {
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + n);
  const p = (x) => String(x).padStart(2, "0");
  return `${dt.getFullYear()}-${p(dt.getMonth() + 1)}-${p(dt.getDate())}`;
}

const ScoreBadge = ({ label }) => {
  const styles = {
    alta: "bg-emerald-100 text-emerald-800 border border-emerald-200 dark:bg-emerald-950/50 dark:text-emerald-300 dark:border-emerald-900/50",
    media: "bg-amber-100 text-amber-800 border border-amber-200 dark:bg-amber-950/50 dark:text-amber-300 dark:border-amber-900/50",
    bassa: "bg-zinc-100 text-zinc-600 border border-zinc-200 dark:bg-zinc-800 dark:text-zinc-300 dark:border-zinc-700",
  };
  return <span className={`text-[10px] font-semibold tracking-wide uppercase px-2 py-0.5 rounded-full ${styles[label] || styles.bassa}`} data-testid={`score-${label}`}>Probabilità {label}</span>;
};

const Kpi = ({ label, value, sub, icon: Icon, accent = "blue", testId }) => {
  const ring = {
    blue: "from-blue-500/10 to-blue-500/0 ring-blue-500/20",
    emerald: "from-emerald-500/10 to-emerald-500/0 ring-emerald-500/20",
    rose: "from-rose-500/10 to-rose-500/0 ring-rose-500/20",
    amber: "from-amber-500/10 to-amber-500/0 ring-amber-500/20",
  }[accent];
  const text = { blue: "text-blue-600", emerald: "text-emerald-600", rose: "text-rose-600", amber: "text-amber-600" }[accent];
  return (
    <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${ring} ring-1 p-5`} data-testid={testId}>
      <div className="flex items-center gap-2 text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">
        <Icon className={`h-3.5 w-3.5 ${text}`} /> {label}
      </div>
      <div className="font-display text-3xl mt-2 tabular-nums dark:text-zinc-100">{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-0.5 dark:text-zinc-400">{sub}</div>}
    </div>
  );
};

export default function SuggestionsPanel({ businessId }) {
  const [stats, setStats] = useState(null);
  const [day, setDay] = useState(todayYmd());
  const [slots, setSlots] = useState([]);
  const [recurring, setRecurring] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [busyKey, setBusyKey] = useState("");
  const [toast, setToast] = useState("");
  const [recurringOpen, setRecurringOpen] = useState(null); // {customer_id, customer_name, ...}

  function showToast(msg) { setToast(msg); setTimeout(() => setToast(""), 3000); }

  async function loadAll() {
    setLoading(true);
    setErr("");
    try {
      const [s, a, r] = await Promise.all([
        api.get(`/businesses/${businessId}/suggestions/stats`),
        api.get(`/businesses/${businessId}/suggestions/agenda-fill`, { params: { day, max_slots: 12, per_slot: 3 } }),
        api.get(`/businesses/${businessId}/suggestions/recurring`),
      ]);
      setStats(s.data);
      setSlots(a.data.slots || []);
      setRecurring(r.data || []);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || "Errore caricamento");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { if (businessId) loadAll(); /* eslint-disable-next-line */ }, [businessId, day]);

  async function sendProposal(slot, candidate) {
    const key = `prop-${slot.start}-${candidate.customer_id}`;
    setBusyKey(key);
    try {
      const { data } = await api.post("/suggestions/send-proposal", {
        business_id: businessId,
        customer_id: candidate.customer_id,
        service_id: slot.service_id,
        start_at: slot.start,
      });
      showToast(data.email_sent ? `Proposta inviata a ${candidate.customer_name} ✓` : `Proposta registrata (email non configurata)`);
      loadAll();
    } catch (e) {
      showToast(formatErr(e.response?.data?.detail) || "Errore invio proposta");
    } finally {
      setBusyKey("");
    }
  }

  async function bookNow(slot, candidate) {
    const key = `book-${slot.start}-${candidate.customer_id}`;
    setBusyKey(key);
    try {
      await api.post("/suggestions/book-now", {
        business_id: businessId,
        customer_id: candidate.customer_id,
        service_id: slot.service_id,
        start_at: slot.start,
        staff_id: candidate.preferred_staff_id || undefined,
      });
      showToast(`Prenotato per ${candidate.customer_name} ✓`);
      loadAll();
    } catch (e) {
      showToast(formatErr(e.response?.data?.detail) || "Errore prenotazione");
    } finally {
      setBusyKey("");
    }
  }

  async function dismiss(slot, candidate) {
    const key = `dis-${slot.start}-${candidate.customer_id}`;
    setBusyKey(key);
    try {
      await api.post("/suggestions/dismiss", {
        business_id: businessId,
        customer_id: candidate.customer_id,
        service_id: slot.service_id,
        slot_start: slot.start,
      });
      // optimistic local removal so the user sees feedback instantly
      setSlots((prev) => prev.map((s) => s.start === slot.start && s.service_id === slot.service_id
        ? { ...s, candidates: s.candidates.filter((c) => c.customer_id !== candidate.customer_id) }
        : s).filter((s) => s.candidates.length > 0));
      showToast("Suggerimento ignorato");
    } catch (e) {
      showToast(formatErr(e.response?.data?.detail) || "Errore");
    } finally {
      setBusyKey("");
    }
  }

  async function dismissRecurring(rec) {
    const key = `disr-${rec.customer_id}`;
    setBusyKey(key);
    try {
      await api.post("/suggestions/dismiss", {
        business_id: businessId,
        customer_id: rec.customer_id,
        service_id: rec.preferred_service_id,
        reason: "recurring_dismiss",
      });
      setRecurring((prev) => prev.filter((r) => r.customer_id !== rec.customer_id));
      showToast("Suggerimento ignorato");
    } catch (e) {
      showToast(formatErr(e.response?.data?.detail) || "Errore");
    } finally {
      setBusyKey("");
    }
  }

  const dayOptions = useMemo(() => Array.from({ length: 14 }, (_, i) => addDaysYmd(todayYmd(), i)), []);

  return (
    <div className="space-y-6" data-testid="suggestions-panel">
      <div>
        <div className="flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase text-blue-600">
          <Sparkles className="h-4 w-4" /> Riempimento agenda
        </div>
        <div className="flex items-center justify-between gap-4 flex-wrap mt-1">
          <h2 className="font-display text-2xl md:text-3xl font-medium dark:text-zinc-100">Consigli prenotazioni</h2>
          <button
            onClick={loadAll}
            className="h-9 px-4 rounded-full bg-zinc-900 text-white text-sm font-medium inline-flex items-center gap-2 hover:bg-zinc-800"
            data-testid="suggestions-refresh"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Aggiorna
          </button>
        </div>
        <p className="text-sm text-zinc-500 mt-1 dark:text-zinc-400">Trova clienti compatibili per gli slot vuoti e rilancia clienti abituali in ritardo.</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="suggestions-kpis">
        <Kpi label="Slot vuoti oggi" value={stats?.open_slots_today ?? "—"} icon={Calendar} accent="amber" testId="kpi-open-slots" />
        <Kpi label="Proposte inviate" value={stats?.proposals_sent ?? "—"} sub={`Ultimi ${stats?.window_days ?? 30}g`} icon={Send} accent="blue" testId="kpi-sent" />
        <Kpi label="Prenotazioni 1-click" value={stats?.one_click_bookings ?? "—"} sub={`+ ${stats?.proposals_accepted ?? 0} accettate`} icon={CheckCircle2} accent="emerald" testId="kpi-booked" />
        <Kpi label="Fatturato recuperato" value={`€${(stats?.recovered_revenue ?? 0).toFixed(0)}`} sub={`Conv. ${stats?.conversion_rate_pct ?? 0}%`} icon={TrendingUp} accent="emerald" testId="kpi-recovered" />
      </div>

      {/* Day picker */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Slot del giorno</span>
        <select value={day} onChange={(e) => setDay(e.target.value)} className="h-9 rounded-full bg-white border border-zinc-200 px-3 text-sm dark:bg-zinc-900 dark:border-zinc-700" data-testid="suggestions-day">
          {dayOptions.map((d) => {
            const dt = new Date(d);
            const lbl = dt.toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "short" });
            return <option key={d} value={d}>{d === todayYmd() ? `Oggi · ${lbl}` : lbl}</option>;
          })}
        </select>
      </div>

      {err && <div className="rounded-xl bg-rose-50 border border-rose-100 text-rose-700 text-sm px-4 py-3 dark:bg-rose-950/30 dark:border-rose-900/50 dark:text-rose-300" data-testid="suggestions-error">{err}</div>}

      {/* Slot suggestions */}
      <section data-testid="suggestions-slots">
        <h3 className="font-display text-lg mb-3 flex items-center gap-2 dark:text-zinc-100">
          <Calendar className="h-4 w-4 text-blue-600" /> Slot da riempire
          <span className="text-xs font-medium text-zinc-500 normal-case dark:text-zinc-400">({slots.length})</span>
        </h3>
        {loading ? (
          <div className="text-sm text-zinc-400 dark:text-zinc-500">Caricamento…</div>
        ) : slots.length === 0 ? (
          <div className="rounded-2xl bg-zinc-50 border border-dashed border-zinc-200 px-6 py-10 text-center text-sm text-zinc-500 dark:bg-zinc-800 dark:border-zinc-700 dark:text-zinc-400" data-testid="slots-empty">
            Nessuno slot libero da riempire in questa data, o nessun cliente compatibile.
          </div>
        ) : (
          <div className="space-y-3">
            {slots.map((slot) => (
              <SlotCard
                key={`${slot.start}-${slot.service_id}`}
                slot={slot}
                busyKey={busyKey}
                onPropose={(c) => sendProposal(slot, c)}
                onBook={(c) => bookNow(slot, c)}
                onDismiss={(c) => dismiss(slot, c)}
              />
            ))}
          </div>
        )}
      </section>

      {/* Recurring late */}
      <section data-testid="suggestions-recurring">
        <h3 className="font-display text-lg mb-3 flex items-center gap-2 dark:text-zinc-100">
          <Repeat className="h-4 w-4 text-violet-600" /> Clienti abituali in ritardo
          <span className="text-xs font-medium text-zinc-500 normal-case dark:text-zinc-400">({recurring.length})</span>
        </h3>
        {loading ? (
          <div className="text-sm text-zinc-400 dark:text-zinc-500">Caricamento…</div>
        ) : recurring.length === 0 ? (
          <div className="rounded-2xl bg-zinc-50 border border-dashed border-zinc-200 px-6 py-10 text-center text-sm text-zinc-500 dark:bg-zinc-800 dark:border-zinc-700 dark:text-zinc-400" data-testid="recurring-empty">
            Nessun cliente abituale è oltre la sua frequenza tipica.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {recurring.map((rec) => (
              <RecurringCard
                key={rec.customer_id}
                rec={rec}
                onPlan={() => setRecurringOpen(rec)}
                onDismiss={() => dismissRecurring(rec)}
                busy={busyKey === `disr-${rec.customer_id}`}
              />
            ))}
          </div>
        )}
      </section>

      {recurringOpen && (
        <RecurringBookingModal
          businessId={businessId}
          rec={recurringOpen}
          onClose={() => setRecurringOpen(null)}
          onCreated={(res) => {
            setRecurringOpen(null);
            showToast(`${res.created_count} appuntamenti creati${res.skipped_count ? ` (${res.skipped_count} saltati)` : ""} ✓`);
            loadAll();
          }}
        />
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] bg-zinc-900 text-white text-sm px-4 py-2.5 rounded-full shadow-lg" data-testid="suggestions-toast">
          {toast}
        </div>
      )}
    </div>
  );
}

function SlotCard({ slot, busyKey, onPropose, onBook, onDismiss }) {
  const dur = slot.duration_min;
  return (
    <div className="rounded-2xl bg-white ring-1 ring-zinc-100 shadow-sm overflow-hidden dark:bg-zinc-900 dark:ring-zinc-800" data-testid={`slot-card-${slot.start}-${slot.service_id}`}>
      <div className="bg-gradient-to-r from-blue-50 to-transparent px-5 py-3 border-b border-zinc-100 flex items-center justify-between gap-3 flex-wrap dark:from-blue-950/30 dark:border-zinc-800">
        <div>
          <div className="text-xs font-semibold tracking-[0.15em] uppercase text-blue-700 dark:text-blue-400">Slot libero</div>
          <div className="font-medium text-zinc-900 capitalize dark:text-zinc-100">
            {fmtDateShort(slot.start)} · <span className="tabular-nums">{new Date(slot.start).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</span>
            <span className="text-zinc-500 font-normal dark:text-zinc-400"> – {new Date(slot.end).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</span>
          </div>
          <div className="text-xs text-zinc-600 mt-0.5 dark:text-zinc-400">{slot.service_name} · {dur} min · €{slot.price.toFixed(0)}</div>
        </div>
      </div>
      <ul className="divide-y divide-zinc-100 dark:divide-zinc-800">
        {slot.candidates.map((c) => {
          const k = `${slot.start}-${c.customer_id}`;
          const propBusy = busyKey === `prop-${k}`;
          const bookBusy = busyKey === `book-${k}`;
          const disBusy = busyKey === `dis-${k}`;
          return (
            <li key={c.customer_id} className="px-5 py-3 flex items-center gap-3 flex-wrap" data-testid={`candidate-${c.customer_id}`}>
              <div className="flex-1 min-w-[220px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-medium text-zinc-900 dark:text-zinc-100">{c.customer_name}</div>
                  <ScoreBadge label={c.score_label} />
                  {c.did_this_service && (
                    <span className="text-[10px] font-semibold uppercase tracking-wide bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full dark:bg-blue-950/50 dark:text-blue-300">Già fatto</span>
                  )}
                  {c.no_show_ratio > 0.2 && (
                    <span className="text-[10px] font-semibold uppercase tracking-wide bg-rose-50 text-rose-700 px-2 py-0.5 rounded-full inline-flex items-center gap-1 dark:bg-rose-950/50 dark:text-rose-300"><AlertTriangle className="h-3 w-3" />No-show {Math.round(c.no_show_ratio * 100)}%</span>
                  )}
                </div>
                <div className="text-xs text-zinc-500 mt-1 dark:text-zinc-400">
                  {c.last_visit ? `Ultima visita: ${c.days_since_last}g fa` : "Mai venuto"}
                  {c.avg_gap_days ? ` · Media: ogni ${Math.round(c.avg_gap_days)}g` : ""}
                  {c.total_visits ? ` · ${c.total_visits} visite` : ""}
                </div>
              </div>
              <div className="flex gap-1.5 flex-wrap">
                <button
                  onClick={() => onPropose(c)}
                  disabled={propBusy || !c.customer_email}
                  title={!c.customer_email ? "Cliente senza email" : "Invia proposta via email/push"}
                  className="h-9 px-3 rounded-full bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-medium inline-flex items-center gap-1.5"
                  data-testid={`btn-propose-${c.customer_id}`}
                >
                  <Mail className="h-3.5 w-3.5" /> {propBusy ? "Invio…" : "Proposta"}
                </button>
                <button
                  onClick={() => onBook(c)}
                  disabled={bookBusy}
                  className="h-9 px-3 rounded-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-xs font-medium inline-flex items-center gap-1.5"
                  data-testid={`btn-book-${c.customer_id}`}
                >
                  <CheckCircle2 className="h-3.5 w-3.5" /> {bookBusy ? "…" : "Prenota subito"}
                </button>
                <button
                  onClick={() => onDismiss(c)}
                  disabled={disBusy}
                  className="h-9 px-3 rounded-full bg-zinc-100 hover:bg-zinc-200 disabled:opacity-40 text-zinc-700 text-xs font-medium inline-flex items-center gap-1 dark:bg-zinc-800 dark:hover:bg-zinc-700 dark:text-zinc-300"
                  data-testid={`btn-dismiss-${c.customer_id}`}
                >
                  <X className="h-3.5 w-3.5" /> Ignora
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function RecurringCard({ rec, onPlan, onDismiss, busy }) {
  const dowLabel = rec.preferred_dow !== null && rec.preferred_dow !== undefined ? WEEKDAYS_IT[rec.preferred_dow] : null;
  const cad = CADENCE_LABELS[rec.cadence] || rec.cadence;
  return (
    <div className="rounded-2xl bg-white ring-1 ring-zinc-100 p-5 flex flex-col gap-3 dark:bg-zinc-900 dark:ring-zinc-800" data-testid={`recurring-card-${rec.customer_id}`}>
      <div>
        <div className="text-xs font-semibold tracking-[0.15em] uppercase text-violet-700 dark:text-violet-400">Cliente {cad}</div>
        <div className="font-display text-lg mt-0.5 dark:text-zinc-100">{rec.customer_name}</div>
        <div className="text-xs text-zinc-500 dark:text-zinc-400">
          {dowLabel && <>Abitudine: <strong className="text-zinc-700 dark:text-zinc-300">{dowLabel}</strong>{rec.preferred_hour !== null ? ` ${String(rec.preferred_hour).padStart(2,"0")}:00` : ""} · </>}
          Media ogni <strong className="text-zinc-700 dark:text-zinc-300">{Math.round(rec.avg_gap_days)}g</strong>
        </div>
      </div>
      <div className="rounded-xl bg-zinc-50 p-3 text-xs space-y-0.5 dark:bg-zinc-800">
        <div><span className="text-zinc-500 dark:text-zinc-400">Ultima visita:</span> <span className="text-zinc-800 font-medium dark:text-zinc-200">{rec.days_since_last}g fa</span> ({fmtDateTime(rec.last_visit)})</div>
        {rec.preferred_service_name && <div><span className="text-zinc-500 dark:text-zinc-400">Servizio tipico:</span> <span className="text-zinc-800 font-medium dark:text-zinc-200">{rec.preferred_service_name}</span> · €{(rec.preferred_service_price ?? 0).toFixed(0)}</div>}
        <div><span className="text-zinc-500 dark:text-zinc-400">Visite totali:</span> {rec.total_visits}</div>
      </div>
      <div className="flex gap-2 mt-auto">
        <button
          onClick={onPlan}
          disabled={!rec.preferred_service_id}
          title={!rec.preferred_service_id ? "Manca servizio tipico" : ""}
          className="flex-1 h-10 rounded-full bg-violet-600 hover:bg-violet-700 disabled:opacity-40 text-white text-sm font-medium inline-flex items-center justify-center gap-1.5"
          data-testid={`btn-plan-recurring-${rec.customer_id}`}
        >
          <Repeat className="h-4 w-4" /> Pianifica ricorrenza
        </button>
        <button
          onClick={onDismiss}
          disabled={busy}
          className="h-10 px-3 rounded-full bg-zinc-100 hover:bg-zinc-200 disabled:opacity-40 text-zinc-700 text-sm font-medium inline-flex items-center justify-center gap-1 dark:bg-zinc-800 dark:hover:bg-zinc-700 dark:text-zinc-300"
          data-testid={`btn-dismiss-recurring-${rec.customer_id}`}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function RecurringBookingModal({ businessId, rec, onClose, onCreated }) {
  // Pre-fill: next preferred dow/hour from today
  const initialDate = useMemo(() => {
    const now = new Date();
    let target = new Date(now);
    if (rec.preferred_dow !== null && rec.preferred_dow !== undefined) {
      const todayDow = (now.getDay() + 6) % 7; // mon=0
      let diff = (rec.preferred_dow - todayDow + 7) % 7;
      if (diff === 0) diff = 7; // next week
      target.setDate(now.getDate() + diff);
    } else {
      target.setDate(now.getDate() + 1);
    }
    target.setHours(rec.preferred_hour ?? 10, 0, 0, 0);
    const p = (n) => String(n).padStart(2, "0");
    return `${target.getFullYear()}-${p(target.getMonth() + 1)}-${p(target.getDate())}T${p(target.getHours())}:${p(target.getMinutes())}`;
  }, [rec]);

  const [start, setStart] = useState(initialDate);
  const [cadence, setCadence] = useState(rec.cadence === "weekly" ? "weekly" : rec.cadence === "biweekly" ? "biweekly" : "monthly");
  const [occurrences, setOccurrences] = useState(4);
  const [skipConflicts, setSkipConflicts] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  // Local preview of dates
  const previewDates = useMemo(() => {
    if (!start) return [];
    const list = [];
    let cur = new Date(start);
    for (let i = 0; i < occurrences; i++) {
      list.push(new Date(cur));
      if (cadence === "weekly") cur.setDate(cur.getDate() + 7);
      else if (cadence === "biweekly") cur.setDate(cur.getDate() + 14);
      else {
        cur = new Date(cur);
        cur.setMonth(cur.getMonth() + 1);
      }
    }
    return list;
  }, [start, cadence, occurrences]);

  function toLocalIso(s) {
    const d = new Date(s);
    const pad = (n) => String(n).padStart(2, "0");
    const tz = -d.getTimezoneOffset();
    const sign = tz >= 0 ? "+" : "-";
    const tzH = pad(Math.floor(Math.abs(tz) / 60));
    const tzM = pad(Math.abs(tz) % 60);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00${sign}${tzH}:${tzM}`;
  }

  async function submit() {
    setErr("");
    setSaving(true);
    try {
      const { data } = await api.post(`/businesses/${businessId}/bookings/recurring`, {
        service_id: rec.preferred_service_id,
        customer_id: rec.customer_id,
        first_start_at: toLocalIso(start),
        cadence,
        occurrences,
        staff_id: rec.preferred_staff_id || null,
        skip_conflicts: skipConflicts,
        notes: "Ricorrenza programmata da Consigli",
      });
      onCreated(data);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || "Errore creazione");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-end md:items-center justify-center p-4" onClick={onClose} data-testid="recurring-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl w-full max-w-lg p-6 card-soft max-h-[90vh] overflow-y-auto dark:bg-zinc-900">
        <div className="text-xs font-semibold tracking-[0.2em] uppercase text-violet-700 dark:text-violet-400">Pianifica ricorrenza</div>
        <h2 className="font-display text-2xl font-medium mt-1 mb-4 dark:text-zinc-100">{rec.customer_name}</h2>

        <div className="rounded-2xl bg-violet-50 border border-violet-100 p-3 mb-4 text-sm space-y-0.5 dark:bg-violet-950/30 dark:border-violet-900/50">
          <div><span className="text-violet-700/70 dark:text-violet-400/80">Servizio:</span> <strong className="dark:text-zinc-100">{rec.preferred_service_name}</strong> · €{(rec.preferred_service_price ?? 0).toFixed(0)}</div>
          <div><span className="text-violet-700/70 dark:text-violet-400/80">Frequenza tipica:</span> <span className="dark:text-zinc-100">ogni {Math.round(rec.avg_gap_days)} giorni</span></div>
        </div>

        <label className="block mb-3">
          <span className="text-[10px] font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Primo appuntamento</span>
          <input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} step="900" className="mt-1 w-full h-12 rounded-xl border border-zinc-300 px-3 text-sm focus:border-violet-500 focus:outline-none dark:border-zinc-600" data-testid="recurring-start" />
        </label>

        <label className="block mb-3">
          <span className="text-[10px] font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Cadenza</span>
          <div className="mt-1 grid grid-cols-3 gap-2">
            {[
              { k: "weekly", label: "Settimanale" },
              { k: "biweekly", label: "Ogni 2 settimane" },
              { k: "monthly", label: "Mensile" },
            ].map((o) => (
              <button
                key={o.k}
                type="button"
                onClick={() => setCadence(o.k)}
                className={`h-10 rounded-xl text-xs font-medium border ${cadence === o.k ? "bg-violet-600 text-white border-violet-600" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400 dark:bg-zinc-900 dark:text-zinc-300 dark:border-zinc-700 dark:hover:border-zinc-500"}`}
                data-testid={`cadence-${o.k}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </label>

        <label className="block mb-3">
          <span className="text-[10px] font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Numero di appuntamenti</span>
          <div className="mt-1 flex items-center gap-2">
            <input type="range" min={2} max={24} value={occurrences} onChange={(e) => setOccurrences(Number(e.target.value))} className="flex-1" data-testid="recurring-occurrences" />
            <span className="text-2xl font-display tabular-nums w-10 text-right">{occurrences}</span>
          </div>
        </label>

        <label className="flex items-center gap-2 text-sm mb-4 cursor-pointer">
          <input type="checkbox" checked={skipConflicts} onChange={(e) => setSkipConflicts(e.target.checked)} className="h-4 w-4 accent-violet-600" data-testid="recurring-skip" />
          <span>Salta automaticamente date occupate o fuori orario</span>
        </label>

        <div className="rounded-xl bg-zinc-50 p-3 mb-4 max-h-44 overflow-y-auto dark:bg-zinc-800">
          <div className="text-[10px] font-semibold tracking-[0.15em] uppercase text-zinc-500 mb-1.5 dark:text-zinc-400">Anteprima ({previewDates.length})</div>
          <ul className="text-xs text-zinc-700 space-y-0.5 capitalize dark:text-zinc-300" data-testid="recurring-preview">
            {previewDates.map((d, i) => (
              <li key={i} className="tabular-nums">
                {String(i + 1).padStart(2, "0")} · {d.toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "short", year: "2-digit" })} {d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
              </li>
            ))}
          </ul>
        </div>

        {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-xl px-3 py-2 mb-3 dark:bg-rose-950/30 dark:border-rose-900/50 dark:text-rose-300" data-testid="recurring-error">{err}</div>}

        <div className="flex gap-2">
          <button onClick={onClose} className="btn-secondary flex-1" data-testid="recurring-cancel">Annulla</button>
          <button onClick={submit} disabled={saving} className="flex-1 h-11 rounded-full bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white text-sm font-semibold" data-testid="recurring-confirm">
            {saving ? "Creo…" : `Crea ${occurrences} appuntamenti`}
          </button>
        </div>
      </div>
    </div>
  );
}
